// Shared read-only formatter for early-retention checklist field values. Mirrors
// the editable widget shapes in form.jsx so the form's read-only view and the
// per-member history matrix render identical text. Returns "—" for empty values.
export function format_field_value(shape, value) {
  if (value === undefined || value === null || value === '') return '—';
  switch (shape) {
    case 'sessions_hours':
      return `${value.sessions ?? 0} sessions, ${value.hours ?? 0} hrs`;
    case 'yes_no_teacher': {
      const names = Array.isArray(value.teachers)
        ? value.teachers.map((t) => t.name || t.teacher_name).filter(Boolean)
        : [];
      return `${value.value || '—'}${names.length ? ` — ${names.join(', ')}` : ''}`;
    }
    case 'group_classes_found': {
      const v = value.value || '—';
      if (Array.isArray(value.classes)) {
        if (!value.classes.length) return v;
        const lines = value.classes.map((c) => {
          const parts = [c.name];
          if (c.teacher_name) parts.push(c.teacher_name);
          if (c.day_of_week) parts.push(`${c.day_of_week} ${c.time || ''}`.trim());
          return parts.filter(Boolean).join(' | ');
        });
        return `${v} — ${lines.join('; ')}`;
      }
      // Back-compat: old free-text shape.
      const classes = [value.class_1, value.class_2].filter(Boolean);
      return `${v}${classes.length ? ` — ${classes.join(', ')}` : ''}`;
    }
    case 'teacher_consistency': {
      const c = value || {};
      const parts = [`Consistent: ${c.consistent || '—'}`];
      if (Array.isArray(c.reasons) && c.reasons.length) {
        parts.push(`Reasons: ${c.reasons.map((r) => r.replace(/_/g, ' ')).join(', ')}`);
      }
      if (Array.isArray(c.primary) && c.primary.length) {
        parts.push(`${c.primary.length} primary`);
      }
      return parts.join(' | ');
    }
    case 'discussion_detection': {
      const topics = Array.isArray(value) ? value : [];
      if (!topics.length) return '—';
      const labels = topics
        .filter((t) => t.data_present || (Array.isArray(t.mentions) && t.mentions.length > 0))
        .map((t) => t.label);
      return labels.length ? labels.join(', ') : '—';
    }
    case 'chat_exchanges': {
      const list = Array.isArray(value) ? value : [];
      if (!list.length) return '—';
      return list
        .map((m) => `${m.date} ${m.sender_name || ''}: ${m.message || ''}`.trim())
        .join('; ');
    }
    case 'membership_timeline': {
      const list = Array.isArray(value) ? value : [];
      if (!list.length) return '—';
      return list.map((e) => `${e.date}: ${e.title}${e.detail ? ` (${e.detail})` : ''}`).join('; ');
    }
    case 'message_teacher': {
      const m = value || {};
      if (!m.message && !m.teacher_uuid) return '—';
      return m.message ? String(m.message).replace(/\s+/g, ' ').trim() : '—';
    }
    case 'message_student': {
      const m = value || {};
      return m.message ? String(m.message).replace(/\s+/g, ' ').trim() : '—';
    }
    case 'teacher_relationships':
      return Array.isArray(value) && value.length
        ? value.map((r) => `${r.teacher_name || '—'} (${r.strength || '—'})`).join(', ')
        : '—';
    case 'repeats': {
      const list = Array.isArray(value.repeats) ? value.repeats : [];
      const count = value.count ?? list.length;
      if (!list.length) return String(count);
      return `${count}: ${list
        .map((r) => `${r.day_time} - ${r.teacher_name || '—'} - ${r.times}x`)
        .join('; ')}`;
    }
    case 'text': {
      // Notes are stored as rich-text HTML; strip tags for plain-text contexts.
      const plain = String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return plain || '—';
    }
    default:
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
}
